<template>
  <v-dialog v-model="internalValue" max-width="900" persistent scrollable>
    <v-card>
      <v-card-title class="text-h5">
        Dokument erstellen
        <v-spacer></v-spacer>
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider></v-divider>

      <v-card-text style="height: 600px" class="pa-0">
        <v-stepper v-model="step" vertical>
          <!-- Step 1: Template Selection -->
          <v-stepper-step :complete="step > 1" step="1">
            Vorlage auswählen
          </v-stepper-step>
          <v-stepper-content step="1">
            <v-alert
              v-if="availableTemplates.length === 0"
              type="info"
              text
              class="mb-3"
            >
              Keine Vorlagen für den Kontext "{{ usageContextLabel }}" gefunden.
              Bitte erstellen Sie zuerst eine Dokumentvorlage.
            </v-alert>
            <v-row v-else>
              <v-col
                v-for="template in availableTemplates"
                :key="template.meta.id"
                cols="12"
                sm="6"
              >
                <v-card
                  outlined
                  :class="{
                    'primary--text': selectedTemplate === template.meta.id,
                  }"
                  :style="{
                    border:
                      selectedTemplate === template.meta.id
                        ? '2px solid currentColor'
                        : undefined,
                  }"
                  @click="selectedTemplate = template.meta.id"
                  hover
                  style="cursor: pointer"
                >
                  <v-card-title class="text-subtitle-1">
                    {{ template.name }}
                    <v-spacer></v-spacer>
                    <v-chip x-small>
                      v{{ template.activeVersion.versionNumber }}
                    </v-chip>
                  </v-card-title>
                  <v-card-subtitle>
                    {{ template.description }}
                  </v-card-subtitle>
                  <v-card-text>
                    <v-chip x-small class="mr-1">
                      <v-icon x-small left>{{
                        template.category && template.category.icon
                          ? template.category.icon
                          : "mdi-shape"
                      }}</v-icon>
                      {{
                        template.category ? template.category.title : "Keine"
                      }}
                    </v-chip>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                color="primary"
                :disabled="!selectedTemplate"
                @click="step = 2"
              >
                Weiter
                <v-icon right>mdi-chevron-right</v-icon>
              </v-btn>
            </v-card-actions>
          </v-stepper-content>

          <!-- Step 2: Record Selection (if applicable) -->
          <v-stepper-step
            :complete="step > 2"
            step="2"
            v-if="supportsMultipleRecords"
          >
            Datensätze auswählen
          </v-stepper-step>
          <v-stepper-content step="2" v-if="supportsMultipleRecords">
            <v-alert type="info" text dense class="mb-3">
              Wählen Sie die Datensätze aus, die im Dokument enthalten sein
              sollen.
            </v-alert>
            <v-data-table
              v-model="selectedRecords"
              :headers="recordHeaders"
              :items="recordOptions"
              item-key="uid"
              show-select
              dense
              class="mb-3"
            >
            </v-data-table>
            <v-card-actions>
              <v-btn text @click="step = 1">
                <v-icon left>mdi-chevron-left</v-icon>
                Zurück
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn
                color="primary"
                :disabled="selectedRecords.length === 0"
                @click="step = 3"
              >
                Weiter
                <v-icon right>mdi-chevron-right</v-icon>
              </v-btn>
            </v-card-actions>
          </v-stepper-content>

          <!-- Step 3: Custom Data Inputs -->
          <v-stepper-step :complete="step > 3" step="3">
            Zusätzliche Daten
          </v-stepper-step>
          <v-stepper-content step="3">
            <v-alert
              v-if="customPlaceholders.length === 0"
              type="success"
              text
              dense
              class="mb-3"
            >
              Alle Platzhalter werden automatisch ausgefüllt. Sie können direkt
              mit der Generierung fortfahren.
            </v-alert>
            <div v-else>
              <v-alert type="info" text dense class="mb-3">
                Die folgenden Platzhalter konnten keinem Datenfeld zugeordnet
                werden. Bitte geben Sie die Werte manuell ein.
              </v-alert>
              <v-form ref="form">
                <v-row>
                  <v-col
                    v-for="placeholder in customPlaceholders"
                    :key="placeholder.tag"
                    cols="12"
                    md="6"
                  >
                    <v-text-field
                      v-if="getFieldType(placeholder.tag) === 'text'"
                      v-model="extraData[placeholder.tag]"
                      :label="placeholder.tag"
                      :required="placeholder.required"
                      outlined
                      hide-details="auto"
                    ></v-text-field>
                    <v-text-field
                      v-else-if="getFieldType(placeholder.tag) === 'date'"
                      v-model="extraData[placeholder.tag]"
                      :label="placeholder.tag"
                      :required="placeholder.required"
                      type="date"
                      outlined
                      hide-details="auto"
                    ></v-text-field>
                    <v-text-field
                      v-else-if="getFieldType(placeholder.tag) === 'number'"
                      v-model.number="extraData[placeholder.tag]"
                      :label="placeholder.tag"
                      :required="placeholder.required"
                      type="number"
                      outlined
                      hide-details="auto"
                    ></v-text-field>
                    <v-textarea
                      v-else-if="getFieldType(placeholder.tag) === 'textarea'"
                      v-model="extraData[placeholder.tag]"
                      :label="placeholder.tag"
                      :required="placeholder.required"
                      outlined
                      rows="3"
                      hide-details="auto"
                    ></v-textarea>
                    <v-text-field
                      v-else
                      v-model="extraData[placeholder.tag]"
                      :label="placeholder.tag"
                      :required="placeholder.required"
                      outlined
                      hide-details="auto"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-form>
            </div>
            <v-card-actions>
              <v-btn text @click="step = supportsMultipleRecords ? 2 : 1">
                <v-icon left>mdi-chevron-left</v-icon>
                Zurück
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn color="primary" @click="step = 4">
                Weiter
                <v-icon right>mdi-chevron-right</v-icon>
              </v-btn>
            </v-card-actions>
          </v-stepper-content>

          <!-- Step 4: Generate -->
          <v-stepper-step :complete="step > 4" step="4">
            Dokument generieren
          </v-stepper-step>
          <v-stepper-content step="4">
            <v-alert type="info" text dense class="mb-3">
              Überprüfen Sie Ihre Auswahl und klicken Sie auf "Generieren", um
              das Dokument zu erstellen.
            </v-alert>
            <v-simple-table dense>
              <template v-slot:default>
                <tbody>
                  <tr>
                    <td><strong>Vorlage:</strong></td>
                    <td>{{ selectedTemplateObject?.name }}</td>
                  </tr>
                  <tr v-if="supportsMultipleRecords">
                    <td><strong>Datensätze:</strong></td>
                    <td>{{ selectedRecords.length }}</td>
                  </tr>
                  <tr>
                    <td><strong>Zugeordnete Felder:</strong></td>
                    <td>{{ mappedFieldCount }}</td>
                  </tr>
                  <tr v-if="Object.keys(extraData).length > 0">
                    <td><strong>Manuelle Eingaben:</strong></td>
                    <td>{{ Object.keys(extraData).length }} Felder</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
            <v-progress-linear
              v-if="generating"
              indeterminate
              class="mt-3"
            ></v-progress-linear>
            <v-alert
              v-if="error"
              type="error"
              text
              dense
              dismissible
              class="mt-3"
            >
              {{ error }}
            </v-alert>
            <v-alert v-if="success" type="success" text dense class="mt-3">
              Dokument erfolgreich erstellt und heruntergeladen!
            </v-alert>
            <v-card-actions>
              <v-btn text @click="step = 3" :disabled="generating">
                <v-icon left>mdi-chevron-left</v-icon>
                Zurück
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn
                color="success"
                @click="generate"
                :loading="generating"
                :disabled="success"
              >
                <v-icon left>mdi-file-document-plus</v-icon>
                Generieren
              </v-btn>
            </v-card-actions>
          </v-stepper-content>
        </v-stepper>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions>
        <v-btn text @click="close" :disabled="generating"> Schließen </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState } from "vuex";
import { MY_ORGANIZATION } from "@/store/modules.js";
import {
  GET_DOCUMENT_TEMPLATES,
  LOG_EXPORT_RUN,
} from "@/store/action-types.js";
import {
  USAGE_CONTEXTS,
  PLACEHOLDER_CONTEXTS,
  DATA_SOURCE_CONFIG,
  detectRequiredContexts,
  processAndDownload,
} from "@/util/documentTemplateService.js";

export default {
  name: "GenerateDocumentDialog",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    usageContext: {
      type: String,
      required: true,
    },
    defaultRecords: {
      type: Array,
      default: () => [],
    },
    defaultExtraData: {
      type: Object,
      default: () => ({}),
    },
    // Optional: supply data directly (e.g. from a process node)
    // If provided, the dialog will NOT fetch from the store
    suppliedData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      step: 1,
      selectedTemplate: null,
      selectedRecords: [],
      cachedRecordOptions: [],
      extraData: {},
      generating: false,
      success: false,
      error: null,
    };
  },
  computed: {
    ...mapState({
      documentTemplates: (state) => state.myOrganization.documentTemplates,
      memberships: (state) => state.admin.membershipsPublic,
      activeOrganization: (state) => state.admin.activeOrganization,
    }),
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    usageContextConfig() {
      return USAGE_CONTEXTS[this.usageContext] || {};
    },
    usageContextLabel() {
      return this.usageContextConfig.label || this.usageContext;
    },
    supportsMultipleRecords() {
      return this.usageContextConfig.supportsMultipleRecords || false;
    },
    primaryPlaceholderContext() {
      // Determine the primary (non-global) context from the usage context config
      const contexts = this.usageContextConfig.placeholderContexts || [];
      return (
        contexts.find((ctx) => {
          const config = PLACEHOLDER_CONTEXTS[ctx];
          return config && !config.isGlobal;
        }) || "member"
      );
    },
    availableTemplates() {
      return this.documentTemplates.filter(
        (t) =>
          t.usageContexts &&
          t.usageContexts.includes(this.usageContext) &&
          t.activeVersion,
      );
    },
    selectedTemplateObject() {
      return this.availableTemplates.find(
        (t) => t.meta.id === this.selectedTemplate,
      );
    },
    recordOptions() {
      return this.cachedRecordOptions;
    },
    recordHeaders() {
      const ctx = this.primaryPlaceholderContext;
      if (ctx === "member") {
        return [
          { text: "Name", value: "displayName" },
          { text: "Dienstgrad", value: "rank" },
          { text: "Dienststellung", value: "position" },
        ];
      }
      if (ctx === "dutyReport") {
        return [
          { text: "Titel", value: "title" },
          { text: "Dienstart", value: "dutyType" },
          { text: "Status", value: "status" },
        ];
      }
      if (ctx === "timeSheet") {
        return [
          { text: "Bezeichnung", value: "title" },
          { text: "Mitarbeiter", value: "userName" },
          { text: "Status", value: "status" },
        ];
      }
      return [];
    },
    placeholderMappings() {
      if (!this.selectedTemplateObject) return {};
      const activeVersion = this.selectedTemplateObject.activeVersion;
      if (!activeVersion) return {};
      return activeVersion.placeholderMappings || {};
    },
    requiredContexts() {
      return detectRequiredContexts(this.placeholderMappings);
    },
    customPlaceholders() {
      if (!this.selectedTemplateObject) return [];
      const activeVersion = this.selectedTemplateObject.activeVersion;
      if (!activeVersion) return [];
      const mappings = activeVersion.placeholderMappings;
      const parsed = activeVersion.parsedPlaceholders;
      if (!parsed || !parsed.simple) return [];

      // If mappings exist, only show placeholders marked as "custom" or "unmapped"
      if (mappings && Object.keys(mappings).length > 0) {
        return parsed.simple.filter((p) => {
          const m = mappings[p.tag];
          return !m || m.status === "custom" || m.status === "unmapped";
        });
      }
      // Legacy: fall back to isKnown check
      return parsed.simple.filter((p) => !p.isKnown);
    },
    mappedFieldCount() {
      const mappings = this.placeholderMappings;
      return Object.values(mappings).filter((m) => m.status === "mapped")
        .length;
    },
    /**
     * Build the globalData object for generateDocument.
     * Contains data for global contexts (e.g. organization).
     */
    globalData() {
      const data = {};
      if (this.suppliedData && this.suppliedData.organization) {
        data.organization = this.suppliedData.organization;
      } else if (this.activeOrganization) {
        data.organization = this.activeOrganization;
      }
      return data;
    },
  },
  watch: {
    value(newVal) {
      if (newVal) {
        this.reset();
        this.initialize();
      }
    },
    selectedRecords: {
      handler() {
        // Skip multi-record step if context doesn't support it
        if (!this.supportsMultipleRecords && this.step === 2) {
          this.step = 3;
        }
      },
      immediate: true,
    },
  },
  methods: {
    async initialize() {
      // Load templates if not already loaded
      if (this.documentTemplates.length === 0) {
        await this.$store.dispatch(
          `${MY_ORGANIZATION}${GET_DOCUMENT_TEMPLATES}`,
          {
            organizationId: this.$route.params.organizationId,
          },
        );
      }

      // If data is supplied via prop, use it directly
      if (this.suppliedData && this.suppliedData.records) {
        this.cachedRecordOptions = this.buildRecordOptions(
          this.suppliedData.records,
        );
      } else {
        // Auto-fetch record data based on primary context
        await this.fetchRecordData();
      }

      // Pre-select records if provided
      if (this.defaultRecords.length > 0) {
        const defaultUids = this.defaultRecords.map((r) => r.uid || r.id);
        this.selectedRecords = this.cachedRecordOptions.filter((r) =>
          defaultUids.includes(r.uid),
        );
      }

      // Pre-fill extra data if provided
      if (Object.keys(this.defaultExtraData).length > 0) {
        this.extraData = { ...this.defaultExtraData };
      }
    },
    /**
     * Fetches record data from the store based on the primary placeholder context.
     */
    async fetchRecordData() {
      const ctx = this.primaryPlaceholderContext;
      const config = DATA_SOURCE_CONFIG[ctx];

      if (!config || config.isGlobal || config.requiresCustomFetch) return;

      // Fetch via store action if available
      if (config.action) {
        const moduleMap = {
          ADMIN: "admin/",
          DUTY_MANAGEMENT: "dutyManagement/",
          ACCOUNTING: "accounting/",
        };
        const prefix = moduleMap[config.actionModule] || "";
        try {
          await this.$store.dispatch(`${prefix}${config.action}`, {
            organizationId: this.$route.params.organizationId,
          });
        } catch (e) {
          console.warn(`Failed to fetch data for context ${ctx}:`, e);
        }
      }

      // Build record options from store state
      if (config.statePath) {
        const stateData = config.statePath
          .split(".")
          .reduce((acc, key) => acc && acc[key], this.$store.state);

        if (Array.isArray(stateData) && stateData.length > 0) {
          this.cachedRecordOptions = this.buildRecordOptions(stateData);
        }
      }
    },
    /**
     * Builds display-friendly record options for the data table.
     */
    buildRecordOptions(records) {
      const ctx = this.primaryPlaceholderContext;

      if (ctx === "member") {
        return records.map((m) => ({
          uid: m.uid || m.id || m.meta?.id,
          displayName: m.user?.displayName || "N/A",
          rank: m.rank?.shortTitle || "",
          position: m.dutyPosition?.shortTitle || "",
          _raw: m,
        }));
      }
      if (ctx === "dutyReport") {
        return records.map((r) => ({
          uid: r.id || r.meta?.id,
          title: r.title || "N/A",
          dutyType: r.dutyType?.title || "",
          status: r.status || "",
          _raw: r,
        }));
      }
      if (ctx === "timeSheet") {
        return records.map((t) => ({
          uid: t.id || t.meta?.id,
          title: t.title || "N/A",
          userName: t.user?.displayName || "",
          status: t.status || "",
          _raw: t,
        }));
      }
      // Generic fallback
      return records.map((r, i) => ({
        uid: r.uid || r.id || r.meta?.id || `record-${i}`,
        title: r.title || r.name || `Datensatz ${i + 1}`,
        _raw: r,
      }));
    },
    /**
     * Gets the full raw records for the selected items.
     */
    getSelectedRawRecords() {
      const ctx = this.primaryPlaceholderContext;

      // If data supplied directly, use the raw records
      if (this.suppliedData && this.suppliedData.records) {
        const selectedUids = new Set(this.selectedRecords.map((sr) => sr.uid));
        return this.suppliedData.records.filter((r) =>
          selectedUids.has(r.uid || r.id || r.meta?.id),
        );
      }

      // Get from store state
      const config = DATA_SOURCE_CONFIG[ctx];
      if (!config || !config.statePath) {
        return this.defaultRecords;
      }

      const stateData = config.statePath
        .split(".")
        .reduce((acc, key) => acc && acc[key], this.$store.state);

      if (!Array.isArray(stateData)) return this.defaultRecords;

      const selectedUids = new Set(this.selectedRecords.map((sr) => sr.uid));
      return stateData.filter((r) => {
        const uid = r.uid || r.id || r.meta?.id;
        return selectedUids.has(uid);
      });
    },
    reset() {
      this.step = 1;
      this.selectedTemplate = null;
      this.selectedRecords = [];
      this.cachedRecordOptions = [];
      this.extraData = {};
      this.generating = false;
      this.success = false;
      this.error = null;
    },
    close() {
      this.internalValue = false;
      this.$emit("close");
    },
    getFieldType(tag) {
      const lower = tag.toLowerCase();
      if (lower.includes("date") || lower.includes("datum")) return "date";
      if (
        lower.includes("number") ||
        lower.includes("nummer") ||
        lower.includes("anzahl")
      )
        return "number";
      if (
        lower.includes("text") ||
        lower.includes("description") ||
        lower.includes("beschreibung")
      )
        return "textarea";
      return "text";
    },
    async generate() {
      this.generating = true;
      this.error = null;

      try {
        const template = this.selectedTemplateObject;
        if (!template || !template.activeVersion) {
          throw new Error("Vorlage nicht gefunden");
        }

        // Get records based on context
        let records = [];
        if (this.supportsMultipleRecords) {
          records = this.getSelectedRawRecords();
        } else {
          records =
            this.defaultRecords.length > 0
              ? this.defaultRecords
              : this.getSelectedRawRecords();
        }

        // Generate filename
        const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
        const filename = `${template.name}_${timestamp}.docx`;

        // Process and download with global data
        await processAndDownload(
          template.activeVersion.filePath,
          template.placeholderContext || this.primaryPlaceholderContext,
          records,
          filename,
          this.extraData,
          this.placeholderMappings,
          this.globalData,
        );

        // Log export run
        const recordIds = records.map((r) => r.uid || r.id || r.meta?.id);
        await this.$store.dispatch(`${MY_ORGANIZATION}${LOG_EXPORT_RUN}`, {
          organizationId: this.$route.params.organizationId,
          templateId: template.meta.id,
          templateVersionNumber: template.activeVersion.versionNumber,
          usageContext: this.usageContext,
          recordIds,
          extraData: this.extraData,
        });

        this.success = true;
        this.$emit("generated", {
          templateId: template.meta.id,
          recordCount: records.length,
        });
      } catch (err) {
        console.error("Error generating document:", err);
        this.error = err.message || "Fehler beim Generieren des Dokuments";
      } finally {
        this.generating = false;
      }
    },
  },
};
</script>
